<script setup lang="ts">
	useSeoMeta({
		title: 'Cookie Policy | MyCoverGenius',
		description: 'Cookie Policy | MyCoverGenius',
		ogTitle: 'Cookie Policy | MyCoverGenius',
		ogDescription: 'Cookie Policy | MyCoverGenius',
		ogType: 'website',
		articleTag: ['Cookies Policy', 'Privacy Policy', 'Cookie Policy'],
		ogUrl: `${import.meta.env.VITE_BUSINESS_URL}/cookie-policy`,
		twitterTitle: 'Cookie Policy | MyCoverGenius',
		twitterDescription: 'Cookie Policy | MyCoverGenius',
		twitterSite: '@mycovergenius',
		twitterAppUrlGoogleplay: import.meta.env.VITE_BUSINESS_URL,
		twitterAppUrlIphone: import.meta.env.VITE_BUSINESS_URL
	})
</script>

<template>
	<div class="wrapper">
		<ui-pad :ui="{ margin: 'md:mt-10', paddingInline: 'md:px-10 px-3' }">
			<article>
				<h1 class="c13" id="h.ora7japljus3">
					<span class="c8">Cookie Policy</span>
				</h1>
				<p class="c10">
					<span class="c9">This Cookie Policy explains how </span><span class="c11">MyCoverGenius</span
					><span class="c6"
						>&nbsp;(&ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) uses cookies and similar
						technologies when you visit or use our websites, mobile applications, and online services
						(&ldquo;Services&rdquo;).<br />
						By continuing to use our Services, you agree to our use of cookies as described in this Policy.
						You can manage or disable cookies through your browser or device settings at any time.</span
					>
				</p>
				<hr />
				<p class="c0"><span class="c6"></span></p>
				<h2 class="c5" id="h.19donyahzwm8">
					<span class="c2">1. What Cookies Are</span>
				</h2>
				<p class="c10">
					<span class="c6"
						>Cookies are small text files that are placed on your device (computer, tablet, or mobile) when
						you visit a website. They help us improve how our Services work, personalize your experience,
						enhance security, and analyze usage patterns.</span
					>
				</p>
				<hr />
				<p class="c0"><span class="c6"></span></p>
				<h2 class="c5" id="h.8gtt2a8rj4h9">
					<span class="c2">2. Types of Cookies We Use</span>
				</h2>
				<h3 class="c1" id="h.t0kqmm2axvx8">
					<span class="c4">a. Essential Cookies</span>
				</h3>
				<p class="c10">
					<span class="c6"
						>These cookies are required for the platform to function properly. They enable core features
						like secure logins, payment processing, and remembering your privacy preferences. If you disable
						these cookies, some parts of our Services may not work as intended.</span
					>
				</p>
				<hr />
				<p class="c0"><span class="c6"></span></p>
				<h3 class="c1 c17" id="h.lngpdr8lpz2b"><span class="c4"></span></h3>
				<h3 class="c1" id="h.4bpxvnyb9dy6">
					<span class="c4">b. Performance &amp; Analytics Cookies</span>
				</h3>
				<p class="c10">
					<span class="c6"
						>These cookies collect anonymous information about how you interact with our Services. We use
						this data to understand user behavior, improve functionality, fix issues, and enhance overall
						performance.</span
					>
				</p>
				<p class="c10">
					<span class="c6">Examples include page views, app usage patterns, and error tracking.</span>
				</p>
				<hr />
				<p class="c0"><span class="c6"></span></p>
				<h3 class="c1" id="h.vyhyxf34f8yr">
					<span class="c4">c. Functional Cookies</span>
				</h3>
				<p class="c10">
					<span class="c6"
						>These cookies remember your preferences to provide a more personalized experience.</span
					>
				</p>
				<p class="c10"><span class="c6">Examples include:</span></p>
				<ul class="c14 lst-kix_gk84g8pxkwup-0 start">
					<li class="c3 li-bullet-0">
						<span class="c6">Remembering your language settings<br /></span>
					</li>
					<li class="c3 li-bullet-0">
						<span class="c6">Saving your login details<br /></span>
					</li>
					<li class="c3 li-bullet-0">
						<span class="c6">Customizing how content is displayed<br /></span>
					</li>
				</ul>
				<hr />
				<p class="c0"><span class="c6"></span></p>
				<h3 class="c1" id="h.4gdnt857a37l">
					<span class="c4">d. Marketing &amp; Advertising Cookies</span>
				</h3>
				<p class="c10">
					<span class="c6"
						>These cookies help us show you relevant offers and measure the effectiveness of our marketing
						efforts.</span
					>
				</p>
				<p class="c10"><span class="c6">Examples include:</span></p>
				<ul class="c14 lst-kix_da8wwfuz9ws3-0 start">
					<li class="c3 li-bullet-0">
						<span class="c6">Displaying personalized promotions<br /></span>
					</li>
					<li class="c3 li-bullet-0">
						<span class="c6">Preventing repetitive ads<br /></span>
					</li>
					<li class="c3 li-bullet-0">
						<span class="c6">Tracking ad performance and engagement<br /></span>
					</li>
				</ul>
				<hr />
				<p class="c0"><span class="c6"></span></p>
				<h2 class="c5" id="h.937ruwo712e7">
					<span class="c2">3. Third-Party Cookies</span>
				</h2>
				<p class="c10">
					<span class="c6"
						>Some cookies may be placed by third parties who provide services on our behalf&mdash;such as
						analytics, advertising, or social media partners. These third parties may collect information
						about your online activities across websites and devices.</span
					>
				</p>
				<p class="c10">
					<span class="c6"
						>We do not control these cookies. For details on how these third parties use your information,
						please refer to their respective cookie or privacy policies.</span
					>
				</p>
				<hr />
				<p class="c0"><span class="c6"></span></p>
				<h2 class="c5" id="h.bvznonsrcp3s">
					<span class="c2">4. Managing Cookies</span>
				</h2>
				<p class="c10">
					<span class="c6"
						>You can manage or disable cookies through your browser or device settings. Please note that if
						you choose to disable certain cookies, parts of the platform may not function properly or as
						intended.</span
					>
				</p>
				<p class="c10">
					<span class="c6">For guidance on managing cookies, visit:</span>
				</p>
				<ul class="c14 lst-kix_mkkjsvxzwyv5-0 start">
					<li class="c3 li-bullet-0">
						<span class="c6">Chrome<br /></span>
					</li>
					<li class="c3 li-bullet-0">
						<span class="c6">Safari<br /></span>
					</li>
					<li class="c3 li-bullet-0">
						<span class="c6">Firefox<br /></span>
					</li>
				</ul>
				<hr />
				<p class="c0"><span class="c6"></span></p>
				<h2 class="c5" id="h.itlx8gx24b7a">
					<span class="c2">5. Updates to This Policy</span>
				</h2>
				<p class="c10">
					<span class="c6"
						>We may update this Cookie Policy from time to time to reflect changes in our practices,
						technology, or legal requirements. When we make changes, we will post the updated version on our
						website and indicate the date of the last update.</span
					>
				</p>
				<hr />
				<p class="c0"><span class="c6"></span></p>
				<h2 class="c5" id="h.gc9lohl05p2m">
					<span class="c2">6. Contact Us</span>
				</h2>
				<p class="c10">
					<span class="c9"
						>If you have questions about our use of cookies or this Policy, you can reach us at:<br /></span
					><span class="c9"
						>&nbsp; hello@mycovergenius.com<br />
						&nbsp;</span
					><span class="c9 c15"
						><a
							class="c7"
							href="https://www.google.com/url?q=https://www.mycovergenius.com&amp;sa=D&amp;source=editors&amp;ust=1761740133318013&amp;usg=AOvVaw1jjb3VcwcqOiwPcX1iMOGL"
							>www.mycovergenius.com</a
						></span
					>
				</p>
				<p class="c0"><span class="c6"></span></p>
			</article>
		</ui-pad>
	</div>
</template>

<style type="text/css" scoped>
	.active {
		@apply font-semibold text-base border-main;
	}
	.lst-kix_gk84g8pxkwup-0 > li:before {
		content: '\0025cf   ';
	}
	.lst-kix_gk84g8pxkwup-1 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_gk84g8pxkwup-2 > li:before {
		content: '\0025a0   ';
	}
	.lst-kix_gk84g8pxkwup-4 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_gk84g8pxkwup-3 > li:before {
		content: '\0025cf';
	}
	.lst-kix_gk84g8pxkwup-5 > li:before {
		content: '\0025a0   ';
	}
	ul.lst-kix_da8wwfuz9ws3-2 {
		list-style-type: none;
	}
	ul.lst-kix_da8wwfuz9ws3-1 {
		list-style-type: none;
	}
	ul.lst-kix_da8wwfuz9ws3-0 {
		list-style-type: none;
	}
	ul.lst-kix_da8wwfuz9ws3-6 {
		list-style-type: none;
	}
	ul.lst-kix_da8wwfuz9ws3-5 {
		list-style-type: none;
	}
	ul.lst-kix_da8wwfuz9ws3-4 {
		list-style-type: none;
	}
	ul.lst-kix_da8wwfuz9ws3-3 {
		list-style-type: none;
	}
	ul.lst-kix_da8wwfuz9ws3-8 {
		list-style-type: none;
	}
	ul.lst-kix_da8wwfuz9ws3-7 {
		list-style-type: none;
	}
	.lst-kix_mkkjsvxzwyv5-7 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_mkkjsvxzwyv5-8 > li:before {
		content: '\0025a0   ';
	}
	ul.lst-kix_gk84g8pxkwup-0 {
		list-style-type: none;
	}
	.lst-kix_mkkjsvxzwyv5-3 > li:before {
		content: '\0025cf   ';
	}
	.lst-kix_mkkjsvxzwyv5-5 > li:before {
		content: '\0025a0   ';
	}
	.lst-kix_mkkjsvxzwyv5-2 > li:before {
		content: '\0025a0   ';
	}
	.lst-kix_mkkjsvxzwyv5-6 > li:before {
		content: '\0025cf   ';
	}
	.lst-kix_mkkjsvxzwyv5-4 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_gk84g8pxkwup-8 > li:before {
		content: '\0025a0   ';
	}
	.lst-kix_gk84g8pxkwup-7 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_gk84g8pxkwup-6 > li:before {
		content: '\0025cf   ';
	}
	.lst-kix_mkkjsvxzwyv5-1 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_mkkjsvxzwyv5-0 > li:before {
		content: '\0025cf   ';
	}
	ul.lst-kix_mkkjsvxzwyv5-1 {
		list-style-type: none;
	}
	ul.lst-kix_mkkjsvxzwyv5-0 {
		list-style-type: none;
	}
	ul.lst-kix_mkkjsvxzwyv5-5 {
		list-style-type: none;
	}
	ul.lst-kix_mkkjsvxzwyv5-4 {
		list-style-type: none;
	}
	ul.lst-kix_mkkjsvxzwyv5-3 {
		list-style-type: none;
	}
	.lst-kix_da8wwfuz9ws3-8 > li:before {
		content: '\0025a0   ';
	}
	ul.lst-kix_mkkjsvxzwyv5-2 {
		list-style-type: none;
	}
	ul.lst-kix_mkkjsvxzwyv5-8 {
		list-style-type: none;
	}
	ul.lst-kix_mkkjsvxzwyv5-7 {
		list-style-type: none;
	}
	ul.lst-kix_mkkjsvxzwyv5-6 {
		list-style-type: none;
	}
	.lst-kix_da8wwfuz9ws3-1 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_da8wwfuz9ws3-0 > li:before {
		content: '\0025cf   ';
	}
	ul.lst-kix_gk84g8pxkwup-8 {
		list-style-type: none;
	}
	ul.lst-kix_gk84g8pxkwup-7 {
		list-style-type: none;
	}
	ul.lst-kix_gk84g8pxkwup-6 {
		list-style-type: none;
	}
	ul.lst-kix_gk84g8pxkwup-5 {
		list-style-type: none;
	}
	ul.lst-kix_gk84g8pxkwup-4 {
		list-style-type: none;
	}
	ul.lst-kix_gk84g8pxkwup-3 {
		list-style-type: none;
	}
	ul.lst-kix_gk84g8pxkwup-2 {
		list-style-type: none;
	}
	ul.lst-kix_gk84g8pxkwup-1 {
		list-style-type: none;
	}
	li.li-bullet-0:before {
		margin-left: -18pt;
		white-space: nowrap;
		display: inline-block;
		min-width: 18pt;
	}
	.lst-kix_da8wwfuz9ws3-2 > li:before {
		content: '\0025a0   ';
	}
	.lst-kix_da8wwfuz9ws3-3 > li:before {
		content: '\0025cf   ';
	}
	.lst-kix_da8wwfuz9ws3-5 > li:before {
		content: '\0025a0   ';
	}
	.lst-kix_da8wwfuz9ws3-4 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_da8wwfuz9ws3-7 > li:before {
		content: '\0025cb   ';
	}
	.lst-kix_da8wwfuz9ws3-6 > li:before {
		content: '\0025cf   ';
	}
	ol {
		margin: 0;
		padding: 0;
	}

	.c3 {
		margin-left: 36pt;
		padding-top: 12pt;
		padding-left: 0pt;
		padding-bottom: 12pt;
		orphans: 2;
		widows: 2;
		text-align: justify;
	}
	.c12 {
		-webkit-text-decoration-skip: none;
		color: #1155cc;
		text-decoration: underline;
		vertical-align: baseline;
		text-decoration-skip-ink: none;
		font-size: 11pt;
		font-style: normal;
	}
	.c8 {
		color: #000000;
		font-weight: 700;
		text-decoration: none;
		vertical-align: baseline;
		font-size: 23pt;
		font-style: normal;
	}
	.c2 {
		color: #000000;
		font-weight: 700;
		text-decoration: none;
		vertical-align: baseline;
		font-size: 17pt;
		font-style: normal;
	}
	.c6 {
		color: #000000;
		font-weight: 400;
		text-decoration: none;
		vertical-align: baseline;
		font-size: 11pt;

		font-style: normal;
	}
	.c4 {
		color: #000000;
		font-weight: 700;
		text-decoration: none;
		vertical-align: baseline;
		font-size: 13pt;

		font-style: normal;
	}
	.c0 {
		padding-top: 0pt;
		padding-bottom: 0pt;
		orphans: 2;
		widows: 2;
		text-align: justify;
		height: 11pt;
	}
	.c5 {
		padding-top: 18pt;
		padding-bottom: 4pt;

		orphans: 2;
		widows: 2;
		text-align: justify;
	}
	.c1 {
		padding-top: 14pt;
		padding-bottom: 4pt;

		orphans: 2;
		widows: 2;
		text-align: justify;
	}
	.c13 {
		padding-top: 24pt;
		padding-bottom: 6pt;

		orphans: 2;
		widows: 2;
		text-align: justify;
	}
	.c10 {
		padding-top: 12pt;
		padding-bottom: 12pt;

		orphans: 2;
		widows: 2;
		text-align: justify;
	}
	.c15 {
		text-decoration-skip-ink: none;
		-webkit-text-decoration-skip: none;
		color: #1155cc;
		text-decoration: underline;
	}
	.c16 {
		background-color: #ffffff;
		max-width: 468pt;
		padding: 72pt 72pt 72pt 72pt;
	}
	.c14 {
		padding: 0;
		margin: 0;
	}
	.c9 {
		font-weight: 400;
	}
	.c7 {
		color: inherit;
		text-decoration: inherit;
	}
	.c11 {
		font-weight: 700;
	}
	.c17 {
		height: 14pt;
	}
	.title {
		padding-top: 0pt;
		color: #000000;
		font-size: 26pt;
		padding-bottom: 3pt;

		page-break-after: avoid;
		orphans: 2;
		widows: 2;
		text-align: left;
	}
	.subtitle {
		padding-top: 0pt;
		color: #666666;
		font-size: 15pt;
		padding-bottom: 16pt;

		page-break-after: avoid;
		orphans: 2;
		widows: 2;
		text-align: left;
	}
	li {
		color: #000000;
		font-size: 11pt;
	}
	p {
		margin: 0;
		color: #000000;
		font-size: 11pt;
	}
	h1 {
		padding-top: 20pt;
		color: #000000;
		font-size: 20pt;
		padding-bottom: 6pt;

		page-break-after: avoid;
		orphans: 2;
		widows: 2;
		text-align: left;
	}
	h2 {
		padding-top: 18pt;
		color: #000000;
		font-size: 16pt;
		padding-bottom: 6pt;

		page-break-after: avoid;
		orphans: 2;
		widows: 2;
		text-align: left;
	}
	h3 {
		padding-top: 16pt;
		color: #434343;
		font-size: 14pt;
		padding-bottom: 4pt;

		page-break-after: avoid;
		orphans: 2;
		widows: 2;
		text-align: left;
	}
	h4 {
		padding-top: 14pt;
		color: #666666;
		font-size: 12pt;
		padding-bottom: 4pt;

		page-break-after: avoid;
		orphans: 2;
		widows: 2;
		text-align: left;
	}
	h5 {
		padding-top: 12pt;
		color: #666666;
		font-size: 11pt;
		padding-bottom: 4pt;

		page-break-after: avoid;
		orphans: 2;
		widows: 2;
		text-align: left;
	}
	h6 {
		padding-top: 12pt;
		color: #666666;
		font-size: 11pt;
		padding-bottom: 4pt;

		page-break-after: avoid;
		font-style: italic;
		orphans: 2;
		widows: 2;
		text-align: left;
	}
</style>
